// Pure geometry for editing markups on a sheet (Larry, Kloepfer 2026-09-15:
// Bluebeam-style node editing, Backspace while drawing, side lengths).
// No Apryse dependency so it can be unit-tested in plain Node — see
// test/javascript/markup_geometry_test.mjs. Points are {x, y} in page units.

// Closest point on segment a→b to p, with the distance and the 0..1 position.
export function distToSegment(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  let t = len2 ? ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2 : 0
  t = Math.max(0, Math.min(1, t))
  const point = { x: a.x + t * dx, y: a.y + t * dy }
  return { d: Math.hypot(p.x - point.x, p.y - point.y), t, point }
}

// Index of the vertex within `tol` of p (nearest wins), or -1.
export function nearestVertex(points, p, tol) {
  let best = -1, bestD = tol
  points.forEach((v, i) => {
    const d = Math.hypot(p.x - v.x, p.y - v.y)
    if (d <= bestD) { bestD = d; best = i }
  })
  return best
}

// The side within `tol` of p. Returns { index, point } where `index` is the
// position a NEW vertex should take (right after side start `index - 1`),
// and `point` is the spot on the side nearest to p. Null when nothing is
// close. `closed` treats the last→first side as a real side (polygons).
export function nearestSegment(points, p, tol, closed) {
  let best = null
  const n = points.length
  const sides = closed ? n : n - 1
  for (let i = 0; i < sides; i++) {
    const a = points[i], b = points[(i + 1) % n]
    const hit = distToSegment(p, a, b)
    if (hit.d <= tol && (!best || hit.d < best.d)) best = { d: hit.d, index: i + 1, point: hit.point }
  }
  return best && { index: best.index, point: best.point }
}

export function withVertexInserted(points, index, point) {
  const out = points.slice()
  out.splice(index, 0, { x: point.x, y: point.y })
  return out
}

export function withVertexRemoved(points, index) {
  return points.filter((_, i) => i !== index)
}

// While a polygon/polyline is being drawn the path is [fixed…, floating]:
// the last point follows the mouse. Backspace takes away ONE line per press
// (Phelipe 2026-09-16, after two rounds): if the floating point is out at
// the cursor, the line to the cursor goes and the point parks on the last
// node; if it is already parked there, the last side goes and the point
// parks on the node before it. `tol` (page units) decides "parked". Null
// when nothing is left to take — the caller cancels the shape.
export function backspacePoints(points, tol = 0) {
  const n = points.length
  if (n < 2) return null
  const floating = points[n - 1], last = points[n - 2]
  const parked = Math.hypot(floating.x - last.x, floating.y - last.y) <= tol
  if (!parked) return points.slice(0, n - 1).concat([{ x: last.x, y: last.y }])
  if (n < 3) return null
  const prev = points[n - 3]
  return points.slice(0, n - 2).concat([{ x: prev.x, y: prev.y }])
}

export function centroid(points) {
  if (!points.length) return { x: 0, y: 0 }
  const s = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 })
  return { x: s.x / points.length, y: s.y / points.length }
}

// One label per side: its midpoint and the scaled length text. Sides
// shorter than half a point are skipped — finishing a polygon with a
// double-click leaves a duplicate last point, which is not a side.
export function segmentLabels(points, closed, factor, unit, precision = 2) {
  const n = points.length
  const sides = closed ? n : n - 1
  const out = []
  for (let i = 0; i < sides; i++) {
    const a = points[i], b = points[(i + 1) % n]
    const raw = Math.hypot(b.x - a.x, b.y - a.y)
    if (raw < 0.5) continue
    const len = raw * factor
    out.push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, text: `${len.toFixed(precision)}${unit ? ` ${unit}` : ""}` })
  }
  return out
}

// Apryse stroke Style → canvas dash array ("dash,4,4" → [4, 4]); solid and
// cloudy give null (cloudy shapes keep Apryse's own painter).
export function dashFor(style) {
  if (!style || typeof style !== "string" || !style.startsWith("dash")) return null
  const nums = style.split(",").slice(1).map(Number).filter(n => isFinite(n) && n > 0)
  return nums.length ? nums : null
}
